import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Space,
  Popconfirm,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';

function PricingManagement() {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchPricing();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchPricing = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/pricing/admin?skip=${(page - 1) * pageSize}&limit=${pageSize}`);
      
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        // Paginated response
        setPricing(response.data.data);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.total
        }));
      } else {
        // Non-paginated response (fallback)
        setPricing(response.data);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.length
        }));
      }
    } catch (error) {
      message.error('Failed to fetch pricing');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPricing(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (pricing) => {
    setEditingPricing(pricing);
    form.setFieldsValue({
      character_limit: pricing.character_limit,
      price: pricing.price,
      description: pricing.description
    });
    setModalVisible(true);
  };

  const handleDelete = async (pricingId) => {
    try {
      await axios.delete(`/api/pricing/${pricingId}`);
      message.success('Pricing deleted successfully');
      fetchPricing();
    } catch (error) {
      message.error('Failed to delete pricing');
    }
  };

  const handleToggleStatus = async (pricingId, isActive) => {
    try {
      await axios.put(`/api/pricing/${pricingId}/toggle`);
      message.success(`Pricing ${isActive ? 'deactivated' : 'activated'}`);
      fetchPricing();
    } catch (error) {
      message.error('Failed to update pricing status');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingPricing) {
        await axios.put(`/api/pricing/${editingPricing.id}`, values);
        message.success('Pricing updated successfully');
      } else {
        await axios.post('/api/pricing/', values);
        message.success('Pricing created successfully');
      }
      setModalVisible(false);
      fetchPricing();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to save pricing');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const formatCharacters = (chars) => {
    return new Intl.NumberFormat('vi-VN').format(chars);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Character Limit',
      dataIndex: 'character_limit',
      key: 'character_limit',
      render: (limit) => `${formatCharacters(limit)} characters`,
      sorter: (a, b) => a.character_limit - b.character_limit,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => formatPrice(price),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type={record.is_active ? 'default' : 'primary'}
            size="small"
            onClick={() => handleToggleStatus(record.id, record.is_active)}
          >
            {record.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this pricing?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Pricing Management</h1>
        <p>Manage text-to-voice pricing plans</p>
      </div>

      <Card>
        <div className="action-buttons">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            className="action-btn"
          >
            Add Pricing
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchPricing}
            className="action-btn"
          >
            Refresh
          </Button>
        </div>

        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={pricing || []}
            rowKey="id"
            loading={loading}
            scroll={{ x: 800 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['10', '20', '50', '100', '200', '500', '1000'],
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
                fetchPricing(page, pageSize);
              },
              onShowSizeChange: (current, size) => {
                setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                fetchPricing(1, size);
              }
            }}
          />
        </div>
      </Card>

      <Modal
        title={editingPricing ? 'Edit Pricing' : 'Add Pricing'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="character_limit"
            label="Character Limit"
            rules={[
              { required: true, message: 'Please input character limit!' },
              { type: 'number', min: 1, message: 'Character limit must be greater than 0!' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="100000"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="price"
            label="Price (VND)"
            rules={[
              { required: true, message: 'Please input price!' },
              { type: 'number', min: 0, message: 'Price must be greater than or equal to 0!' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="100000"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: 'Please input description!' },
              { max: 255, message: 'Description must be less than 255 characters!' }
            ]}
          >
            <Input.TextArea
              placeholder="Enter pricing description..."
              rows={3}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingPricing ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default PricingManagement;